/*
 * Copyright (c) 2023. Tooling Team
 */

import PropTypes from "prop-types";
import { Button } from "@uhg-abyss/web/ui/Button";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Grid } from "@uhg-abyss/web/ui/Grid";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ROW_TAGS } from "../../utils/constants";
import {
  buildBulkEditPayload,
  getFilteredRowData,
  isLocalCodeInTable,
} from "../../utils/funcs.js";
import { FormProvider } from "@uhg-abyss/web/ui/FormProvider";
import { useForm } from "@uhg-abyss/web/hooks/useForm";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { Alert } from "@uhg-abyss/web/ui/Alert";
import { useDispatch, useSelector } from "react-redux";
import { cloneSlice, resetFilters } from "../../app/slices/clone.slice";
import {
  useGetAttrConfigQuery,
  useLazyGetAttributesQuery,
} from "../../app/services/attributesApi";
import { useGetClientListQuery } from "../../app/services/clientApi";
import { getClientNameDropdownValues } from "../../utils/clientDropdown";
import {
  getMappingStatusDropdownValues,
  UNCHANGED_STATUS_VALUE,
} from "./mappingStatusOptions";

const defaultHelperText = "* This field is required.";
const blankHelperText = "* This field cannot be blank.";
const alreadyExistsHelperText =
  "* Local Code and Local System combination already exists in the table.";
const noFieldEditedText = "No fields have been edited.";
const genErrorMsgText = "Please correct the error(s) below.";
const cloneRequiredText =
  "Client, Domain, and Standard Code System are required for clone.";
const cloneValidationErrorText = "Validation failed for one or more rows.";
const localSourceSystemErrorMsgText =
  "LocalSourceSystem is inconsistent with others used for this map. Please update this record's localSourceSystem to ";
const UNCHANGED = UNCHANGED_STATUS_VALUE;

const renderStandardCodeOption = (item) => {
  if (!item.description || item.value === UNCHANGED) {
    return <span>{item.label}</span>;
  }

  return (
    <span>
      {item.label} ({item.description})
    </span>
  );
};

//form for Add, Edit, Bulk Edit, and Clone rows in the map value table. When in Clone mode, also includes fields to select the destination of the cloned rows (Client, Domain, Standard Code System)
export default function EIPAddEditRowForm(props) {
  const { isOpen, onClose, records, formCallback, formData } = props;

  // Form Field State
  const [localCode, setLocalCode] = useState("");
  const [localDescription, setLocalDescription] = useState("");
  const [standardDescription, setStandardDescription] = useState("");
  const [standardCode, setStandardCode] = useState("");
  const [standardCodeSystem, setStandardCodeSystem] = useState("");
  const [localSourceSystem, setLocalSourceSystem] = useState("");
  const [comments, setComments] = useState("");
  const [status, setStatus] = useState("");
  const [dataSourceValue, setDataSourceValue] = useState("");
  const [synSourceValue, setSynSourceValue] = useState("");

  // Dropdown Values
  const [dataSourceValues, setDataSourceValues] = useState([]);
  const [synSourceValues, setSynSourceValues] = useState([]);
  const [statusDropdownValues, setStatusDropdownValues] = useState([]);
  const [standardCodeDropdownValues, setStandardCodeDropdownValues] = useState(
    []
  );
  const [standardCodeSearch, setStandardCodeSearch] = useState("");
  const standardCodeSearchDebounceRef = useRef(null);

  const handleStandardCodeInputChange = useCallback((inputValue) => {
    clearTimeout(standardCodeSearchDebounceRef.current);
    standardCodeSearchDebounceRef.current = setTimeout(() => {
      setStandardCodeSearch(inputValue ?? "");
    }, 250);
  }, []);

  const filteredStandardCodeOptions = useMemo(() => {
    const q = standardCodeSearch.toLowerCase();
    if (!q) return standardCodeDropdownValues;
    // Match label, value, and description so searchConfig's keys stay effective.
    return standardCodeDropdownValues.filter((opt) =>
      ["label", "value", "description"].some((key) =>
        String(opt[key] ?? "")
          .toLowerCase()
          .replace(/[()]/g, "")
          .includes(q)
      )
    );
  }, [standardCodeSearch, standardCodeDropdownValues]);

  // Form Mode State
  const [isBulkEditForm, setIsBulkEditForm] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const [isAddForm, setIsAddForm] = useState(false);
  const [isCloneForm, setIsCloneForm] = useState(false);
  const [formTitle, setFormTitle] = useState("");

  // Clone destination state
  const [cloneClient, setCloneClient] = useState("");
  const [cloneDomain, setCloneDomain] = useState("");
  const [cloneStandardCodeSystem, setCloneStandardCodeSystem] = useState("");
  const [cloneSearchValues, setCloneSearchValues] = useState({});
  const [cloneAttributeLists, setCloneAttributeLists] = useState([]);

  // Error Handling State
  const [isError, setIsError] = useState(false);
  const [genErrorMsg, setGenErrorMsg] = useState(genErrorMsgText);
  const [localCodeIsError, setLocalCodeIsError] = useState(false);
  const [localCodeHelperText, setLocalCodeHelperText] =
    useState(defaultHelperText);

  // Row State
  const [id, setId] = useState("");
  const [bulkEditRows, setBulkEditRows] = useState([]);
  const [addRowId, setAddRowId] = useState("");
  const [isFormSaving, setIsFormSaving] = useState(false);

  const setLocalCodeError = (isError, errorText) => {
    setIsError(isError);
    setLocalCodeIsError(isError);
    setLocalCodeHelperText(errorText);
  };

  const setErrorMsg = (isError, errorText) => {
    setGenErrorMsg(errorText);
    setIsError(isError);
  };

  //localSourceSystem is defined as different "Types of empty" depending on where it's coming from
  //input it is an empty string ("")
  //output from db it is 'null'
  //still other times it is 'undefined'
  //this is a helper function to better compare if a localSourceSystem is actually changed or if it's just a different kind of empty.
  const localSourceSystemContinuestoBeEmpty = (
    localSourceSystem,
    localSourceSystemFromDB
  ) => {
    return !localSourceSystem && !localSourceSystemFromDB;
  };

  const localSourceSystemIsInconsistent = (
    localSourceSystem,
    dataSourceValue,
    synSourceValue
  ) => {
    let consistentLocalSourceSystem;
    const dataSrcChanged = dataSourceValue !== formData.dataSourceValue;
    const synSrcChanged = synSourceValue !== formData.synSourceValue;

    //we do not validate consistency if there is no ds or ss selected, any localSourceSystem is fine
    if (!synSourceValue && !dataSourceValue) {
      return false;
    }
    //call filter row fetching util func
    const filteredRows = getFilteredRowData(
      props.records,
      dataSourceValue,
      synSourceValue
    );

    //get the consistent localSourceSystem already present in the records
    //only concerned about consistency in multiple rows of data
    //adding to a table with 1 row or moving that 1 row to a different ds/ss
    if (
      filteredRows.length > 1 ||
      (filteredRows.length === 1 && isAddForm) ||
      (filteredRows.length === 1 &&
        (dataSrcChanged || synSrcChanged) &&
        isEditForm)
    ) {
      consistentLocalSourceSystem = filteredRows[0].inputCode1;

      //"empty" localSourceSystem in input form is "".
      // output (from db) is null instead, so we need to have an extra check
      if (
        localSourceSystem !== consistentLocalSourceSystem &&
        !localSourceSystemContinuestoBeEmpty(
          localSourceSystem,
          consistentLocalSourceSystem
        )
      ) {
        if (!consistentLocalSourceSystem) {
          consistentLocalSourceSystem = "an empty value";
        }
        setErrorMsg(
          true,
          localSourceSystemErrorMsgText + consistentLocalSourceSystem + "."
        );
        return true;
      }
      return false;
    }

    //other cases, like only one row in the table, or no rows in the table, all localSourceSystem are fine
    return false;
  };

  const validateCloneSubmission = async (cloneRows) => {
    if (!cloneClient || !cloneDomain || !cloneStandardCodeSystem) {
      setErrorMsg(true, cloneRequiredText);
      return;
    }

    const attributes = {
      Client: cloneClient,
      Domain: cloneDomain,
      Standard_Code_System: cloneStandardCodeSystem,
    };

    const cloneValidation = props.cloneValidation || (() => true);
    let allRowsValid = false;

    try {
      allRowsValid = await cloneValidation({
        attributes,
        cloneRows,
      });
    } catch (error) {
      allRowsValid = false;
    }

    const cloneValidationFailed =
      allRowsValid !== true && allRowsValid?.valid !== true;
    if (cloneValidationFailed) {
      setErrorMsg(true, allRowsValid?.message || cloneValidationErrorText);
      return;
    }

    setErrorMsg(false, genErrorMsgText);
    formCallback({
      attributes,
      searchValues: {
        ...cloneSearchValues,
        ...attributes,
      },
      attributeLists: cloneAttributeLists,
      cloneRows,
    });
  };

  const validateSubmission = async () => {
    if (
      !isCloneForm &&
      localSourceSystem === UNCHANGED &&
      localCode === UNCHANGED &&
      localDescription === UNCHANGED &&
      standardCodeSystem === UNCHANGED &&
      standardDescription === UNCHANGED &&
      comments === UNCHANGED &&
      standardCode === UNCHANGED &&
      status === UNCHANGED &&
      dataSourceValue === UNCHANGED &&
      synSourceValue === UNCHANGED
    ) {
      setErrorMsg(true, noFieldEditedText);
      return;
    }

    if (!isBulkEditForm && localCode === "") {
      setLocalCodeError(true, blankHelperText);
      return;
    }

    if (formData.localCode !== localCode) {
      let cleanSourceSystem = "";
      //issue with 'UNCHANGED' instead of empty string being sent to function
      if (localSourceSystem !== UNCHANGED) {
        cleanSourceSystem = localSourceSystem;
      }
      console.log("local key: " + cleanSourceSystem + " " + localCode);
      if (isLocalCodeInTable(records, cleanSourceSystem, localCode)) {
        setLocalCodeError(true, alreadyExistsHelperText);
        return;
      }
    }

    if (isEditForm && !isCloneForm) {
      if (
        localCode === formData.localCode &&
        localDescription === formData.localDescription &&
        standardDescription === formData.standardDescription &&
        standardCodeSystem === formData.standardCodeSystem &&
        localSourceSystem === formData.localSourceSystem &&
        comments === formData.comments &&
        standardCode === formData.standardCode &&
        status === formData.status &&
        dataSourceValue === formData.dataSourceValue &&
        synSourceValue === formData.synSourceValue
      ) {
        setErrorMsg(true, noFieldEditedText);
        return;
      }
    }

    // Fall through assumes validation passed
    setLocalCodeError(false, defaultHelperText);

    const tags = getTagsAddEdit(dataSourceValue, synSourceValue);
    let validationError = false;

    if (isAddForm) {
      formCallback([
        {
          localCode: localCode,
          localDescription: localDescription,
          standardCode: standardCode,
          standardDescription: getStandardDescription({}),
          standardLongDescription: getStandardLongDescription({}),
          localSourceSystem: localSourceSystem,
          status: status,
          comments: comments,
          lastEditedAt: formData.lastEditedAt,
          lastEditedBy: formData.lastEditedBy,
          _id: id,
          addRowId: addRowId,
          tags,
        },
      ]);
    } else {
      const isDSTagChanged = dataSourceValue !== UNCHANGED;
      const isSSTagChanged = synSourceValue !== UNCHANGED;
      const updatedRecords = [];
      bulkEditRows.forEach((row) => {
        const tags = getTagsBulkEdit(row, dataSourceValue, synSourceValue);
        // TODO - Have separate function to check LocalSourceSystem inconsistency for DS + SS for Bulk edit
        // Current validation works fine only for a selected scenario: Filter the map value rows for a particular Data source tag and do bulk edit across those records
        // If user going to bulk edit records across different data source tags (which should be very rare corner case) then this validation won't work as expected
        if (
          (isDSTagChanged || isSSTagChanged) &&
          localSourceSystemIsInconsistent(
            row.inputCode1,
            getCurrentDS(tags),
            getCurrentSS(tags)
          )
        ) {
          validationError = true;
          return false;
        }
        // Check the case where only syn source is modified and localSourceSystem is not consistent across the editing records
        // This is separate check from localSourceSystemIsInconsistent func because it checks only the existing data and not current bulk edit set.
        if (
          isSSTagChanged &&
          !isDSTagChanged &&
          localSourceSystem !== row.inputCode1
        ) {
          validationError = true;
          setErrorMsg(
            true,
            localSourceSystemErrorMsgText + localSourceSystem + "."
          );
          return false;
        }
        const updatedRecord = {
          localSourceSystem: ternaryCond(
            localSourceSystem !== UNCHANGED,
            localSourceSystem,
            row.localSourceSystem
          ),
          localCode: ternaryCond(
            localCode !== UNCHANGED,
            localCode,
            row.localCode
          ),
          localDescription: ternaryCond(
            localDescription !== UNCHANGED,
            localDescription,
            row.localDescription
          ),
          standardCode: ternaryCond(
            standardCode !== UNCHANGED,
            standardCode,
            row.standardCode
          ),
          status: ternaryCond(status !== UNCHANGED, status, row.status),
          comments: ternaryCond(comments !== UNCHANGED, comments, row.comments),
          standardDescription: getStandardDescription(row),
          standardLongDescription: getStandardLongDescription(row),
          standardCodeSystem: row.standardCodeSystem,
          tags,
        };

        if (isCloneForm) {
          updatedRecords.push({
            ...updatedRecord,
            standardCodeSystem: cloneStandardCodeSystem,
          });
          return;
        }
        updatedRecords.push({
          ...updatedRecord,
          lastEditedAt: formData.lastEditedAt,
          lastEditedBy: formData.lastEditedBy,
          _id: row._id,
          addRowId: addRowId,
          update: true,
        });
      });

      if (validationError) {
        return;
      }

      if (isCloneForm) {
        await validateCloneSubmission(updatedRecords);
        return;
      }

      const bulkEdit = buildBulkEditPayload(
        bulkEditRows,
        {
          localSourceSystem,
          localCode,
          localDescription,
          standardCode,
          standardDescription: ternaryCond(
            standardCode !== UNCHANGED,
            getStandardDescription(bulkEditRows[0] || {}),
            UNCHANGED
          ),
          standardLongDescription: ternaryCond(
            standardCode !== UNCHANGED,
            getStandardLongDescription(bulkEditRows[0] || {}),
            UNCHANGED
          ),
          status,
          comments,
        },
        {
          selection: formData.bulkEditSelection,
        }
      );

      const submission = bulkEdit
        ? { bulkEdit, rows: updatedRecords }
        : updatedRecords;

      await formCallback(submission);
    }

    if (validationError) {
      return;
    }
  };

  const saveRow = async () => {
    setIsFormSaving(true);
    try {
      await validateSubmission();
    } finally {
      setIsFormSaving(false);
    }
  };

  const setStatusDropdown = (event) => {
    if (
      isBulkEditForm &&
      !statusDropdownValues.some((item) => item.value === event)
    ) {
      setStatus(UNCHANGED);
      return;
    }
    setStatus(event);
  };

  const setDataSourceDropdown = (event) => {
    setDataSourceValue(event);
  };

  const setSynSourceDropdown = (event) => {
    setSynSourceValue(event);
  };

  const setStandardCodeDropdown = (event) => {
    // Drop any pending/applied search text so the full option list returns after select or clear.
    clearTimeout(standardCodeSearchDebounceRef.current);
    setStandardCodeSearch("");
    setStandardCode(event);
  };
  //populate standard description based on choices in the dialog- even if we haven't saved as draft yet
  const getStandardDescription = (row) => {
    if (standardCode === UNCHANGED) {
      return row.standardDescription;
    }

    return (
      props.getStandardCodeDetails?.(standardCode)?.description ??
      row.standardDescription
    );
  };

  const getStandardLongDescription = (row) => {
    if (standardCode === UNCHANGED) {
      return row.standardLongDescription;
    }

    const standardCodeDetails = props.getStandardCodeDetails?.(standardCode);
    if (standardCodeDetails?.longDescription !== undefined) {
      return standardCodeDetails.longDescription;
    }

    const selectedStandardCode = standardCodeDropdownValues.find(
      (item) => item.value === standardCode
    );

    return selectedStandardCode?.longDescription ?? row.standardLongDescription;
  };

  const initStatusDropdown = (mode) => {
    const statusDropdownValues = getStatusDropdownValues(mode);
    setStatusDropdownValues(statusDropdownValues);

    const statusDropdownValue =
      statusDropdownValues.filter(function (item) {
        return item.value === formData.status;
      })[0] || statusDropdownValues[0];

    setStatus(statusDropdownValue.value);
  };

  const initMapValueTagsDropdown = (
    mode,
    mapValueTags,
    source,
    value,
    setSource,
    setValue
  ) => {
    const sourceDropdownValues = getSourceDropdownValues(mode);
    setValue(sourceDropdownValues[0].value);

    const sourceTag = mapValueTags.filter(function (item) {
      return item.name === source;
    })[0];

    if (sourceTag) {
      sourceTag.values.forEach((element, index) => {
        const obj = {
          id: index + 1,
          label: `${element}`,
          value: element,
        };
        if (element === value) {
          setValue(obj.value);
        }
        sourceDropdownValues.push(obj);
      });
      setSource(sourceDropdownValues);
    }
  };

  const initStandardCodeDropdown = (mode, standardCodeDropdownItems) => {
    const standardCodeDropdownValues = standardCodeDropdownItems;
    setStandardCodeDropdownValues(standardCodeDropdownValues);

    const standardCodeDropdownValue =
      standardCodeDropdownItems.filter(function (item) {
        return item.value === formData.standardCode;
      })[0] || standardCodeDropdownValues[0];

    setStandardCode(standardCodeDropdownValue.value);
  };

  useEffect(() => {
    const {
      mode,
      _id,
      localCode,
      localDescription,
      standardCodeDropdownItems,
      standardDescription,
      standardCodeSystem,
      localSourceSystem,
      comments,
      addRowId,
      dataSourceValue,
      synSourceValue,
      mapValueTags,
      rows,
      standardCode,
      status,
    } = formData;

    // Set Form Mode
    if (mode === "Clone") {
      setIsCloneForm(true);
      setIsBulkEditForm(true);
      setIsEditForm(true);
      setIsAddForm(false);
      setFormTitle(`${mode} (${rows.length} Rows)`);
      setBulkEditRows(rows);
    } else if (mode === "Bulk Edit") {
      if (rows.length > 1) {
        setIsBulkEditForm(true);
      }
      setIsCloneForm(false);
      setFormTitle(`${mode} (${rows.length} Rows)`);
      setBulkEditRows(rows);
      setIsEditForm(true);
    } else if (mode === "Add") {
      setIsCloneForm(false);
      setIsAddForm(true);
      setIsBulkEditForm(false);
      setIsEditForm(false);
      setFormTitle(`${mode} Row`);
    } else {
      setIsCloneForm(false);
      setIsEditForm(true);
      setFormTitle(`${mode} Row`);
      setBulkEditRows(rows);
      setIsBulkEditForm(false);
    }

    setId(_id);
    setLocalCode(localCode);
    setLocalDescription(localDescription);
    setStandardDescription(standardDescription);
    setStandardCodeSystem(standardCodeSystem);
    setLocalSourceSystem(localSourceSystem);
    setComments(comments);
    setStatus(status);
    setStandardCode(standardCode);
    setAddRowId(addRowId);

    // Initialize Dropdown Values
    initStatusDropdown(mode);
    initStandardCodeDropdown(mode, standardCodeDropdownItems);

    // Data Source
    initMapValueTagsDropdown(
      mode,
      mapValueTags,
      ROW_TAGS.DATA_SOURCE,
      dataSourceValue,
      setDataSourceValues,
      setDataSourceValue
    );

    // Syn Source
    initMapValueTagsDropdown(
      mode,
      mapValueTags,
      ROW_TAGS.SYNONYM_SOURCE,
      synSourceValue,
      setSynSourceValues,
      setSynSourceValue
    );
  }, []);

  const form = useForm({ defaultValues: formData });

  const footer = (
    <Layout.Group>
      {" "}
      <Button
        onClick={saveRow}
        isLoading={isFormSaving}
        ariaLoadingLabel={isCloneForm ? "Validating clone" : "Saving row"}
      >
        Save
      </Button>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    </Layout.Group>
  );

  return (
    <FormProvider state={form}>
      <ModalDialog
        isOpen={isOpen}
        header={formTitle}
        model="model-form"
        onClose={onClose}
        footer={footer}
      >
        <Grid>
          {isCloneForm ? (
            <CloneDestinationFields
              isOpen={isOpen}
              client={cloneClient}
              domain={cloneDomain}
              standardCodeSystem={cloneStandardCodeSystem}
              sourceMapLocation={props.sourceMapLocation}
              onClientChange={setCloneClient}
              onDomainChange={setCloneDomain}
              onStandardCodeSystemChange={setCloneStandardCodeSystem}
              onSearchValuesChange={setCloneSearchValues}
              onAttributeListsChange={setCloneAttributeLists}
            />
          ) : null}
          {!isBulkEditForm && formData.mapValueTags.length !== 0 ? (
            <Grid.Col span={12}>
              <SelectInput
                label="Data Source"
                isClearable
                isSearchable
                value={dataSourceValue}
                onChange={setDataSourceDropdown}
                options={dataSourceValues}
              />
              <SelectInput
                label="Synonym Source"
                isClearable
                isSearchable
                value={synSourceValue}
                onChange={setSynSourceDropdown}
                options={synSourceValues}
              />
            </Grid.Col>
          ) : (
            ""
          )}
          <Grid.Col span={12}>
            {isError && (
              <Alert variant={"error"} id={"notification-error"}>
                {genErrorMsg}
              </Alert>
            )}{" "}
            <TextInput
              label="Local System"
              placeholder={ternaryCond(isBulkEditForm, UNCHANGED, "")}
              value={localSourceSystem}
              onChange={(e) => setLocalSourceSystem(e.target.value)}
            />
            <TextInput
              label="Local Code"
              placeholder={ternaryCond(isBulkEditForm, UNCHANGED, "")}
              value={localCode}
              onChange={(e) => setLocalCode(e.target.value)}
              error={localCodeIsError}
              isDisabled={isBulkEditForm}
              validators={{ required: true }}
            />
            <p className="text-danger">
              {isBulkEditForm ? "" : localCodeHelperText}
            </p>
            <TextInput
              label="Local Description"
              value={localDescription}
              onChange={(e) => setLocalDescription(e.target.value)}
            />
            <SelectInput
              label="Standard Code"
              isClearable
              isSearchable
              value={standardCode}
              onChange={setStandardCodeDropdown}
              onInputChange={handleStandardCodeInputChange}
              options={filteredStandardCodeOptions}
              customRender={renderStandardCodeOption}
              searchConfig={{ keys: ["label", "value", "description"] }}
            />
            <SelectInput
              label="Status"
              isClearable={false}
              isSearchable
              value={status}
              onChange={setStatusDropdown}
              options={statusDropdownValues}
              validators={{ required: true }}
            />
            <TextInput
              label="Comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </Grid.Col>
        </Grid>
      </ModalDialog>
    </FormProvider>
  );
}

function CloneDestinationFields({
  isOpen,
  client,
  domain,
  standardCodeSystem,
  sourceMapLocation,
  onClientChange,
  onDomainChange,
  onStandardCodeSystemChange,
  onSearchValuesChange,
  onAttributeListsChange,
}) {
  const { cAttrConfig, cAttrFilters } = useSelector(
    (state) => state[cloneSlice.reducerPath] || {}
  );
  const dispatch = useDispatch();
  const [triggerGetAttributes] = useLazyGetAttributesQuery();
  const [searchValues, setSearchValues] = useState({});
  const { data: clientListResponse } = useGetClientListQuery(undefined, {
    skip: !isOpen,
  });
  const clientDropdownValues = useMemo(() => {
    return getClientNameDropdownValues(clientListResponse);
  }, [clientListResponse]);

  useGetAttrConfigQuery("clone", { skip: !isOpen });

  useEffect(() => {
    onAttributeListsChange(cAttrFilters || []);
  }, [cAttrFilters, onAttributeListsChange]);

  const getFilter = (type) => {
    return cAttrFilters?.find((obj) => obj.name === type);
  };

  const getItems = (type) => {
    return getFilter(type)?.values || [];
  };

  const domainOptions = useMemo(() => {
    const domains = getItems("Domain");

    if (client !== sourceMapLocation?.client) {
      return domains;
    }

    return domains.filter(
      (option) => option.value !== sourceMapLocation?.domain
    );
  }, [cAttrFilters, client, sourceMapLocation]);

  const updateCloneFilter = (event, attrObj, newSearchValues) => {
    if (!attrObj) {
      return;
    }

    dispatch(resetFilters({ attrObj, searchValues, newSearchValues }));
    setSearchValues(newSearchValues);
    onSearchValuesChange(newSearchValues);

    if (attrObj.child && cAttrConfig?._id) {
      triggerGetAttributes({
        configId: cAttrConfig._id,
        attrName: attrObj.child,
        filters: newSearchValues,
        moduleName: "clone",
      });
    }
  };

  const setClientDropdown = (event) => {
    const newSearchValues = { Client: event };
    onClientChange(event);
    onDomainChange("");
    onStandardCodeSystemChange("");
    updateCloneFilter(event, getFilter("Client"), newSearchValues);
  };

  const setDomainDropdown = (event) => {
    const newSearchValues = {
      ...searchValues,
      Domain: event,
    };
    delete newSearchValues.Standard_Code_System;
    onDomainChange(event);
    onStandardCodeSystemChange("");
    updateCloneFilter(event, getFilter("Domain"), newSearchValues);
  };

  const setStandardCodeSystemDropdown = (event) => {
    const newSearchValues = {
      ...searchValues,
      Standard_Code_System: event,
    };
    onStandardCodeSystemChange(event);
    updateCloneFilter(
      event,
      getFilter("Standard_Code_System"),
      newSearchValues
    );
  };

  return (
    <Grid.Col span={12}>
      <SelectInput
        id="clone-client"
        label="Client"
        validators={{ required: true }}
        model="cloneClient"
        value={client}
        options={clientDropdownValues}
        onChange={setClientDropdown}
        isSearchable
      />
      <SelectInput
        key={`clone-domain-${client || "empty"}`}
        id="clone-domain"
        label="Domain"
        validators={{ required: true }}
        model="cloneDomain"
        value={domain}
        options={domainOptions}
        onChange={setDomainDropdown}
        isSearchable
      />
      <SelectInput
        key={`clone-standard-code-system-${client || "empty"}-${
          domain || "empty"
        }`}
        id="clone-standard-code-system"
        label="Standard Code System"
        validators={{ required: true }}
        model="cloneStandardCodeSystem"
        value={standardCodeSystem}
        options={getItems("Standard_Code_System")}
        onChange={setStandardCodeSystemDropdown}
        isSearchable
      />
    </Grid.Col>
  );
}

export const getTagsBulkEdit = (row, dataSourceValue, synSourceValue) => {
  const tags = [];

  if (dataSourceValue === UNCHANGED) {
    const currDataSourceValue = row.tags
      ? row.tags.filter(function (item) {
          return item.name === ROW_TAGS.DATA_SOURCE;
        })[0]
      : null;

    if (currDataSourceValue) {
      tags.push({
        name: ROW_TAGS.DATA_SOURCE,
        values: currDataSourceValue.values,
      });
    }
  } else if (dataSourceValue) {
    tags.push({
      name: ROW_TAGS.DATA_SOURCE,
      values: [dataSourceValue],
    });
  }

  if (synSourceValue === UNCHANGED) {
    const currSynSourceValue = row.tags
      ? row.tags.filter(function (item) {
          return item.name === ROW_TAGS.SYNONYM_SOURCE;
        })[0]
      : null;

    if (currSynSourceValue) {
      tags.push({
        name: ROW_TAGS.SYNONYM_SOURCE,
        values: currSynSourceValue.values,
      });
    }
  } else if (synSourceValue) {
    tags.push({
      name: ROW_TAGS.SYNONYM_SOURCE,
      values: [synSourceValue],
    });
  }
  return tags;
};

export const getTagsAddEdit = (dataSourceValue, synSourceValue) => {
  const tags = [];
  if (dataSourceValue) {
    tags.push({
      name: ROW_TAGS.DATA_SOURCE,
      values: [dataSourceValue],
    });
  }
  if (synSourceValue) {
    tags.push({
      name: ROW_TAGS.SYNONYM_SOURCE,
      values: [synSourceValue],
    });
  }
  return tags;
};

export const getCurrentDS = (tags) => {
  const currDataSourceValue = tags
    ? tags.filter(function (item) {
        return item.name === ROW_TAGS.DATA_SOURCE;
      })[0]
    : null;
  return currDataSourceValue
    ? currDataSourceValue.values[0]
    : currDataSourceValue;
};

export const getCurrentSS = (tags) => {
  const currSynSourceValue = tags
    ? tags.filter(function (item) {
        return item.name === ROW_TAGS.SYNONYM_SOURCE;
      })[0]
    : null;
  return currSynSourceValue ? currSynSourceValue.values[0] : currSynSourceValue;
};

export const getStatusDropdownValues = getMappingStatusDropdownValues;

export const getSourceDropdownValues = (mode) => {
  const dropDownValues = [];

  if (mode === "Bulk Edit" || mode === "Clone") {
    dropDownValues.push({
      id: -2,
      label: UNCHANGED,
      value: UNCHANGED,
    });
  }
  dropDownValues.push({ id: -1, label: "", value: "" });
  return dropDownValues;
};

// Stupid but effective function to reduce Sonar "code complexity" issue
export const ternaryCond = (condition, ifTrue, ifFalse) => {
  return condition ? ifTrue : ifFalse;
};

EIPAddEditRowForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  records: PropTypes.array,
  formCallback: PropTypes.func,
  formData: PropTypes.object,
  cloneValidation: PropTypes.func,
  getStandardCodeDetails: PropTypes.func,
  sourceMapLocation: PropTypes.shape({
    client: PropTypes.string,
    domain: PropTypes.string,
  }),
};

CloneDestinationFields.propTypes = {
  isOpen: PropTypes.bool,
  client: PropTypes.string,
  domain: PropTypes.string,
  standardCodeSystem: PropTypes.string,
  sourceMapLocation: PropTypes.shape({
    client: PropTypes.string,
    domain: PropTypes.string,
  }),
  onClientChange: PropTypes.func,
  onDomainChange: PropTypes.func,
  onStandardCodeSystemChange: PropTypes.func,
  onSearchValuesChange: PropTypes.func,
  onAttributeListsChange: PropTypes.func,
};
